import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    ChangeEvent,
} from 'react';
import {
    Checkbox,
    TextInput,
    Button,
    Group,
    Stack,
    ScrollArea,
    Box,
    Text,
    Loader,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';

interface ColumnFilterMenuProps {
    filterOptions: string[];
    currentValue: string[] | null;
    onChange: (value: string[] | null) => void;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    menuWidth?: number;
    onSearch?: (query: string) => void;
    options?: string[];
    loading?: boolean;
}

export const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = React.memo(
    ({
        filterOptions,
        currentValue,
        onChange,
        setIsOpen,
        menuWidth = 350,
        onSearch,
        options,
        loading = false,
    }) => {
        const initialSelectedOptions = useMemo(() => {
            if (!currentValue) return [];
            if (currentValue.length === 0) return ['All'];
            const nonAllFilterOptions = filterOptions.filter(
                opt => opt !== 'All',
            );
            if (
                nonAllFilterOptions.length > 0 &&
                nonAllFilterOptions.every(opt => currentValue.includes(opt))
            ) {
                return ['All', ...currentValue];
            }
            return currentValue;
        }, [currentValue, filterOptions]);

        const [searchValue, setSearchValue] = useState('');
        const [debouncedSearchValue] = useDebouncedValue(searchValue, 300);
        const [selectedOptions, setSelectedOptions] = useState<string[]>(
            initialSelectedOptions,
        );

        const isTypeahead = onSearch && filterOptions.length === 0;
        const filteredOptions = useMemo(() => {
            if (isTypeahead) {
                return options ?? [];
            }
            if (!debouncedSearchValue) return filterOptions;
            return filterOptions.filter(option =>
                option
                    .toLowerCase()
                    .includes(debouncedSearchValue.toLowerCase()),
            );
        }, [filterOptions, debouncedSearchValue, isTypeahead, options]);

        useEffect(() => {
            if (isTypeahead && onSearch) {
                onSearch(debouncedSearchValue);
            }
        }, [debouncedSearchValue, isTypeahead, onSearch]);

        const isAllSelected = useMemo(() => {
            const nonAllOptions = filterOptions.filter(opt => opt !== 'All');
            return (
                nonAllOptions.length > 0 &&
                nonAllOptions.every(opt => selectedOptions.includes(opt))
            );
        }, [selectedOptions, filterOptions]);

        const handleSelectAll = useCallback(() => {
            setSelectedOptions([
                'All',
                ...filterOptions.filter(opt => opt !== 'All'),
            ]);
        }, [filterOptions]);

        const handleDeselectAll = useCallback(() => {
            setSelectedOptions([]);
        }, []);

        const handleOptionChange = useCallback(
            (option: string, checked: boolean) => {
                if (option === 'All') {
                    if (checked) {
                        handleSelectAll();
                    } else {
                        handleDeselectAll();
                    }
                    return;
                }

                setSelectedOptions(prev => {
                    let newSelectedBase = prev.filter(opt => opt !== 'All');

                    if (checked) {
                        newSelectedBase = [...newSelectedBase, option];
                    } else {
                        newSelectedBase = newSelectedBase.filter(
                            opt => opt !== option,
                        );
                    }

                    const nonAllFilterOptions = filterOptions.filter(
                        opt => opt !== 'All',
                    );
                    if (
                        nonAllFilterOptions.length > 0 &&
                        nonAllFilterOptions.every(opt =>
                            newSelectedBase.includes(opt),
                        )
                    ) {
                        return ['All', ...newSelectedBase];
                    }
                    return newSelectedBase;
                });
            },
            [filterOptions, handleSelectAll, handleDeselectAll],
        );

        const handleApply = useCallback(() => {
            if (
                selectedOptions.includes('All') ||
                selectedOptions.length === 0
            ) {
                onChange(null);
            } else {
                onChange(selectedOptions.filter(opt => opt !== 'All'));
            }
            setIsOpen(false);
        }, [selectedOptions, onChange, setIsOpen]);

        const handleClose = useCallback(() => {
            if (!currentValue || currentValue.length === 0) {
                setSelectedOptions(['All']);
            } else {
                const nonAllFilterOptions = filterOptions.filter(
                    opt => opt !== 'All',
                );
                if (
                    nonAllFilterOptions.length > 0 &&
                    nonAllFilterOptions.every(opt => currentValue.includes(opt))
                ) {
                    setSelectedOptions(['All', ...currentValue]);
                } else {
                    setSelectedOptions(currentValue ?? []);
                }
            }
            setSearchValue('');
            setIsOpen(false);
        }, [currentValue, filterOptions, setIsOpen]);

        const handleClearFilter = useCallback(() => {
            setSelectedOptions(['All']);
            onChange(null);
            setSearchValue('');
        }, [onChange]);

        useEffect(() => {
            if (currentValue === null) {
                setSelectedOptions([
                    'All',
                    ...filterOptions.filter(opt => opt !== 'All'),
                ]);
            } else {
                const nonAllFilterOptions = filterOptions.filter(
                    opt => opt !== 'All',
                );
                if (
                    nonAllFilterOptions.every(opt =>
                        currentValue.includes(opt),
                    ) &&
                    nonAllFilterOptions.length > 0
                ) {
                    setSelectedOptions(['All', ...currentValue]);
                } else {
                    setSelectedOptions(currentValue ?? []);
                }
            }
        }, [currentValue, filterOptions]);

        const safeSelectedOptions = selectedOptions ?? [];

        return (
            <Stack
                gap="xs"
                p="sm"
                style={{ minWidth: `${menuWidth}px` }}
                className={`max-h-[400px] min-w-[${menuWidth}px] overflow-hidden`}
                onClick={e => e.stopPropagation()}
            >
                <Group gap={10} wrap="nowrap" className="w-full">
                    <TextInput
                        placeholder="Search..."
                        value={searchValue}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            e.stopPropagation();
                            setSearchValue(e.currentTarget.value);
                        }}
                        leftSection={<IconSearch size={16} />}
                        size="sm"
                        className="flex-[0_1_70%]"
                    />
                    <Button
                        variant="subtle"
                        color="red"
                        size="xs"
                        onClick={e => {
                            e.stopPropagation();
                            handleClearFilter();
                        }}
                        aria-label="Clear filter"
                        leftSection={<IconX size={16} />}
                        className="flex-[1_1_30%] min-w-0"
                    >
                        Clear
                    </Button>
                </Group>

                <Box className="flex-1 min-h-0 bg-primary/10" py="xs" px="sm">
                    <ScrollArea className="h-[200px] max-h-[250px]">
                        {loading ? (
                            <div className="flex items-center justify-center h-[200px]">
                                <Loader size="sm" />
                            </div>
                        ) : (
                            <Stack gap="xs">
                                {!isTypeahead && (
                                    <Checkbox
                                        label="(Select All)"
                                        checked={isAllSelected}
                                        onChange={e => {
                                            if (e.currentTarget.checked) {
                                                handleSelectAll();
                                            } else {
                                                handleDeselectAll();
                                            }
                                        }}
                                        onClick={e => e.stopPropagation()}
                                        size="sm"
                                    />
                                )}
                                {filteredOptions.length === 0 &&
                                    (isTypeahead && !debouncedSearchValue ? (
                                        <Text size="sm" c="dimmed">
                                            Type to search...
                                        </Text>
                                    ) : (
                                        <Text>No results</Text>
                                    ))}
                                {filteredOptions.length > 0 &&
                                    filteredOptions
                                        .filter(option => option !== 'All')
                                        .map(option => (
                                            <Checkbox
                                                key={option}
                                                label={
                                                    isTypeahead
                                                        ? option.replace(
                                                              /^0+/,
                                                              '',
                                                          )
                                                        : option
                                                }
                                                checked={safeSelectedOptions.includes(
                                                    option,
                                                )}
                                                onChange={e => {
                                                    handleOptionChange(
                                                        option,
                                                        e.currentTarget.checked,
                                                    );
                                                }}
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                                size="sm"
                                            />
                                        ))}
                            </Stack>
                        )}
                    </ScrollArea>
                </Box>

                <Group
                    justify="space-between"
                    className="w-full"
                    wrap="nowrap"
                    mt="sm"
                >
                    <Button
                        className="text-primary border-primary hover:bg-primary/10"
                        variant="outline"
                        size="xs"
                        onClick={e => {
                            e.stopPropagation();
                            handleClose();
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        size="xs"
                        onClick={e => {
                            e.stopPropagation();
                            handleApply();
                        }}
                    >
                        Apply
                    </Button>
                </Group>
            </Stack>
        );
    },
);

ColumnFilterMenu.displayName = 'ColumnFilterMenu';
